import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdChevronLeft, MdChevronRight, MdRefresh } from 'react-icons/md';
import { FiDownload } from 'react-icons/fi';
import { GoHash } from 'react-icons/go';
import { BsBuilding, BsCalendar2 } from 'react-icons/bs';
import { TbEdit, TbTrash } from 'react-icons/tb';
import { IoClose } from 'react-icons/io5';
import { motion, AnimatePresence } from 'framer-motion';

import { FiFileText, FiCalendar } from 'react-icons/fi';
import { BsCurrencyRupee } from 'react-icons/bs';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import ResponsiveTable from '../components/Table/ResponsiveTable';
import { TableSkeleton } from '../components/Table/TableSkeleton';
import PopupWrapper from '../components/utils/PopupWrapper';
import { usePopup } from '../hooks/usePopup';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchInvoices, searchInvoices, sendDeleteInvoiceOTP, deleteInvoiceWithOTP } from '../redux/actions/invoiceActions';
import axiosInstance from '../axios';
import { hasBillProjectPermission } from '../utils/permissionUtils';
import toast from 'react-hot-toast';

// Interface for billing data (formatted for table display)
interface BillingItem {
  id: string;
  mId: string[];
  customerName: string;
  billNumber: string;
  status: 'Billed';
  billingDate: string;
  projectNames: string[];
  subtotal: number;
  balanceDue: number;
}

interface FormattedBillingItem {
  id: string;
  mId: string[];
  customerName: string;
  billNumber: string;
  status: 'Billed';
  paymentState: 'paid' | 'partial' | 'unpaid';
  paymentSummary: {
    totalProjects: number;
    paymentReceivedProjects: number;
    allProjectsPaymentReceived: boolean;
    hasAnyPaymentReceived: boolean;
    hasPartialPaymentReceived: boolean;
  };
  billingDate: {
    date: string;
    time: string;
  };
  billingDateRaw?: string;
  projectNames: string[];
  subtotal: number;
  balanceDue: number;
}

// Interface for invoice line items
interface InvoiceLineItem {
  id: number;
  description: string;
  mid: string;
  articleNo: string;
  quantity: number;
  rate: number;
  amount: number;
}

// Interface for invoice data
interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  purchaseOrder: string;
  terms: string;
  taxType: 'IGST' | 'CGST_SGST';
  companyDetails: {
    name: string;
    address: string;
    email: string;
    phone: string;
    gst: string;
    cin?: string;
  };
  customerDetails: {
    name: string;
    contactPerson: string;
    address: string;
    gst: string;
  };
  lineItems: InvoiceLineItem[];
  subtotal: number;
  discount: number;
  discountPercent: number;
  tax: number;
  taxPercent: number;
  cgst?: number;
  sgst?: number;
  amountPaid: number;
  grandTotal: number;
  totalInWords: string;
  notes: string;
  termsAndCondition: string;
}

// Function to convert number to words (Indian numbering system)
const numberToWords = (num: number): string => {
  if (num === 0) return 'Zero Rupees Only';

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertLessThanThousand = (n: number): string => {
    if (n === 0) return '';
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) {
      const ten = Math.floor(n / 10);
      const one = n % 10;
      return tens[ten] + (one > 0 ? ' ' + ones[one] : '');
    }
    const hundred = Math.floor(n / 100);
    const remainder = n % 100;
    return ones[hundred] + ' Hundred' + (remainder > 0 ? ' ' + convertLessThanThousand(remainder) : '');
  };

  if (num < 0) return 'Negative amount';

  // Indian numbering system: Crores, Lakhs, Thousands, Hundreds
  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const remainder = num % 1000;

  let result = '';

  if (crore > 0) {
    result += convertLessThanThousand(crore) + ' Crore ';
  }
  if (lakh > 0) {
    result += convertLessThanThousand(lakh) + ' Lakh ';
  }
  if (thousand > 0) {
    result += convertLessThanThousand(thousand) + ' Thousand ';
  }
  if (remainder > 0) {
    result += convertLessThanThousand(remainder);
  }

  return result.trim() + ' Rupees Only';
};

// Component to render dropdown for multiple project IDs
const ProjectIdDropdown: React.FC<{ mIds: string[] }> = ({ mIds }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  if (mIds.length === 1) {
    return <span className="font-medium text-gray-900 bg-red-50 px-2 py-1 rounded">{mIds[0]}</span>;
  }

  return (
    <>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation(); // Prevent row click
          setIsOpen(!isOpen);
        }}
        className="flex items-center justify-between w-full font-medium text-gray-900 hover:text-blue-600 transition-colors bg-red-50 px-2 py-1 rounded"
      >
        <span>{mIds[0]}, M...</span>
        {isOpen ? (
          <MdKeyboardArrowUp className="w-4 h-4" />
        ) : (
          <MdKeyboardArrowDown className="w-4 h-4" />
        )}
      </button>

      {isOpen && buttonRef.current && (
        <div
          ref={dropdownRef}
          className="fixed bg-red-50 border border-red-200 rounded shadow-lg min-w-[200px]"
          style={{
            top: buttonRef.current.getBoundingClientRect().bottom + 4,
            left: buttonRef.current.getBoundingClientRect().left,
            zIndex: 999999,
          }}
          onClick={(e) => e.stopPropagation()} // Prevent row click when clicking inside dropdown
        >
          <div className="p-2">
            <div className="space-y-1">
              {mIds.map(id => (
                <div key={id} className="text-sm">
                  <span className="font-medium text-gray-900">{id}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const BillingHistory: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAppSelector(state => state.auth);
  const {
    invoices,
    searchInvoices: searchResults,
    pagination,
    searchPagination,
    fetchingInvoices,
    searchingInvoices,
    error
  } = useAppSelector(state => state.invoice);

  // Check if user has billing permission
  const hasBillingPermission = hasBillProjectPermission(user);

  // Refs to prevent loops - CRITICAL
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchKey = useRef<string>('');

  // Calculate default dates ONCE using a ref (not useMemo/useCallback to avoid dependency chains)
  const defaultDatesRef = useRef<{ startDate: string; endDate: string } | null>(null);
  if (!defaultDatesRef.current) {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const formatDate = (d: Date) => {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    defaultDatesRef.current = {
      startDate: formatDate(firstDay),
      endDate: formatDate(today),
    };
  }
  const defaultDates = defaultDatesRef.current;

  // Read current params from URL (with stable defaults from ref)
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const itemsPerPage = parseInt(searchParams.get('limit') || '10', 10);
  const selectedSeries = (searchParams.get('series') || 'MESPL') as 'MESPL' | 'MESS' | 'ALL';
  const searchValue = searchParams.get('search') || '';
  const excludePaymentReceived = searchParams.get('excludePaymentReceived') === 'true';
  const includePaymentReceived = searchParams.get('includePaymentReceived') === 'true';
  const includeDuePaymentPending = searchParams.get('includeDuePaymentPending') === 'true';
  const startDateParam = includeDuePaymentPending
    ? ''
    : searchParams.get('startDate') || defaultDates.startDate;
  const endDateParam = includeDuePaymentPending
    ? ''
    : searchParams.get('endDate') || defaultDates.endDate;
  const searchParamsKey = searchParams.toString();

  // Parse date range for UI
  const dateRange = {
    startDate: startDateParam ? new Date(startDateParam) : null,
    endDate: endDateParam ? new Date(endDateParam) : null,
  };

  // Helper function to format date for API
  const formatDateForAPI = (date: Date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Fetch when URL params change
  useEffect(() => {
    if (!hasBillingPermission) return;

    // Initialize defaults when page opens without query params.
    // This rerender (searchParamsKey change) will then trigger fetch.
    if (searchParamsKey === '') {
      setSearchParams(
        {
          page: '1',
          limit: '10',
          series: 'MESPL',
          startDate: defaultDates.startDate,
          endDate: defaultDates.endDate,
        },
        { replace: true }
      );
      return;
    }

    // Create a unique key for current params
    const fetchKey = `${currentPage}-${itemsPerPage}-${selectedSeries}-${searchValue}-${startDateParam}-${endDateParam}-${excludePaymentReceived}-${includePaymentReceived}-${includeDuePaymentPending}`;
    
    // Skip if we already fetched with these exact params
    if (fetchKey === lastFetchKey.current) {
      return;
    }
    lastFetchKey.current = fetchKey;

    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    const params: any = {
      page: currentPage,
      limit: itemsPerPage,
      series: selectedSeries,
      excludePaymentReceived,
      includePaymentReceived,
      includeDuePaymentPending,
    };
    if (startDateParam) params.startDate = startDateParam;
    if (endDateParam) params.endDate = endDateParam;

    if (searchValue.trim()) {
      params.search = searchValue.trim();
      dispatch(searchInvoices(params));
    } else {
      dispatch(fetchInvoices(params));
    }
  }, [
    searchParamsKey,
    currentPage,
    itemsPerPage,
    selectedSeries,
    searchValue,
    startDateParam,
    endDateParam,
    excludePaymentReceived,
    includePaymentReceived,
    includeDuePaymentPending,
    dispatch,
    hasBillingPermission,
    setSearchParams,
    defaultDates.startDate,
    defaultDates.endDate,
  ]);

  // Helper to update URL params
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // URL param setters
  const setCurrentPage = useCallback((page: number) => {
    updateParams({ page: page.toString() });
  }, [updateParams]);

  const setItemsPerPage = useCallback((limit: number) => {
    updateParams({ limit: limit.toString(), page: '1' });
  }, [updateParams]);

  const setSelectedSeries = useCallback((series: 'MESPL' | 'MESS' | 'ALL') => {
    updateParams({ series, page: '1' });
  }, [updateParams]);

  const setSearchValue = useCallback((search: string) => {
    updateParams({ search: search || null, page: '1' });
  }, [updateParams]);

  const setDateRangeParams = useCallback((range: { startDate: Date | null; endDate: Date | null }) => {
    updateParams({
      startDate: range.startDate ? formatDateForAPI(range.startDate) : null,
      endDate: range.endDate ? formatDateForAPI(range.endDate) : null,
      includeDuePaymentPending: null,
      page: '1',
    });
  }, [updateParams]);

  // Local UI state
  const [displayData, setDisplayData] = useState<FormattedBillingItem[]>([]);
  const [searchInputValue, setSearchInputValue] = useState(searchValue);

  // Sync search input with URL
  useEffect(() => {
    setSearchInputValue(searchValue);
  }, [searchValue]);

  // Debounced search handler
  const handleSearchInputChange = useCallback((value: string) => {
    setSearchInputValue(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchValue(value);
    }, 500);
  }, [setSearchValue]);

  // Date picker UI state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selecting, setSelecting] = useState<'start' | 'end' | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempDate, setTempDate] = useState<Date | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Invoice popup state
  const invoicePopup = usePopup();
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceData | null>(null);

  // Delete invoice modal state (Super Admin only)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInvoiceData, setDeleteInvoiceData] = useState<{ id: string; billNumber: string; customerName: string } | null>(null);
  const [deleteStep, setDeleteStep] = useState<'confirm' | 'otp'>('confirm');
  const [otpValue, setOtpValue] = useState('');
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [otpError, setOtpError] = useState('');

  // Check if user is super admin
  const normalizedDesignation =
    (typeof user?.designation === 'string' ? user.designation : user?.designation?.name || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
  const normalizedRole = (user?.role || '').trim().toUpperCase();
  const normalizedUserType = (user?.userType || '').trim().toUpperCase();
  const isSuperAdmin =
    user?.isSuperAdmin === true ||
    normalizedDesignation === 'superadmin' ||
    normalizedRole === 'ADMIN' ||
    normalizedUserType === 'ADMIN';

  // Handle outside clicks to close calendar
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper functions for date picker
  const formatDateForDisplay = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);
    const days: (Date | null)[] = Array(firstDayOfMonth).fill(null);
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const isInRange = (date: Date) => {
    if (!date || !dateRange.startDate || !dateRange.endDate) return false;
    const time = date.getTime();
    const start = dateRange.startDate.getTime();
    const end = dateRange.endDate.getTime();
    return time >= start && time <= end;
  };

  const handleDateClick = (date: Date) => {
    if (!date) return;

    if (!selecting || selecting === 'start') {
      setTempDate(date);
      setSelecting('end');
    } else if (selecting === 'end') {
      const firstDate = tempDate as Date;
      if (firstDate.getTime() <= date.getTime()) {
        setDateRangeParams({ startDate: firstDate, endDate: date });
      } else {
        setDateRangeParams({ startDate: date, endDate: firstDate });
      }
      setTempDate(null);
      setSelecting(null);
    }
  };

  const changeMonth = (increment: number) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + increment);
    setCurrentMonth(newMonth);
  };

  const resetDateRange = () => {
    // Reset to default: 1st of current month to today
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateRangeParams({ startDate: firstDayOfMonth, endDate: today });
    setSelecting(null);
    setTempDate(null);
    setCurrentMonth(new Date());
    toast.success('Date filter reset to current month');
  };

  // Reset ALL filters to defaults
  const resetAllFilters = useCallback(() => {
    // Clear search input
    setSearchInputValue('');
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Reset URL to defaults
    setSearchParams({
      page: '1',
      limit: '10',
      series: 'MESPL',
      startDate: defaultDates.startDate,
      endDate: defaultDates.endDate,
    }, { replace: true });
    
    // Reset date picker UI state
    setSelecting(null);
    setTempDate(null);
    setCurrentMonth(new Date());
    
    // Clear the lastFetchKey to force a new fetch
    lastFetchKey.current = '';
    
    toast.success('All filters reset to defaults');
  }, [setSearchParams, defaultDates]);

  useEffect(() => {
    if (!includeDuePaymentPending) {
      return;
    }

    if (searchParams.get('startDate') || searchParams.get('endDate')) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('startDate');
        next.delete('endDate');
        return next;
      }, { replace: true });
    }
  }, [includeDuePaymentPending, searchParams, setSearchParams]);

  // Check if any filter is active (non-default)
  const hasActiveFilters = useMemo(() => {
    const isSearchActive = searchValue.trim() !== '';
    const isSeriesChanged = selectedSeries !== 'MESPL';
    const isDateChanged = includeDuePaymentPending
      ? false
      : startDateParam !== defaultDates.startDate || endDateParam !== defaultDates.endDate;
    const isPaymentFilterChanged =
      excludePaymentReceived || includePaymentReceived || includeDuePaymentPending;
    const isPageChanged = currentPage !== 1;
    const isLimitChanged = itemsPerPage !== 10;
    
    return (
      isSearchActive ||
      isSeriesChanged ||
      isDateChanged ||
      isPaymentFilterChanged ||
      isPageChanged ||
      isLimitChanged
    );
  }, [
    searchValue,
    selectedSeries,
    startDateParam,
    endDateParam,
    includeDuePaymentPending,
    excludePaymentReceived,
    includePaymentReceived,
    currentPage,
    itemsPerPage,
    defaultDates,
  ]);

  // Invoice date = invoiceDate (same field the date filter and invoice popup use);
  // the time shown is when the invoice was actually created.
  const formatBillingDateTime = (invoiceDate?: string, createdAt?: string) => {
    const dateSource = invoiceDate || createdAt;
    if (!dateSource) {
      return { date: '-', time: '-' };
    }

    const date = new Date(dateSource);
    if (Number.isNaN(date.getTime())) {
      return { date: '-', time: '-' };
    }

    // invoiceDate is stored as a date-only value (midnight UTC), so read it in UTC;
    // a createdAt fallback is a real timestamp and is read in IST.
    const dateParts = new Intl.DateTimeFormat('en-IN', {
      timeZone: invoiceDate ? 'UTC' : 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).formatToParts(date);

    const day = dateParts.find(part => part.type === 'day')?.value ?? '00';
    const month = dateParts.find(part => part.type === 'month')?.value ?? '00';
    const year = dateParts.find(part => part.type === 'year')?.value ?? '0000';

    const created = createdAt ? new Date(createdAt) : null;

    return {
      date: `${day}-${month}-${year}`,
      time:
        created && !Number.isNaN(created.getTime())
          ? new Intl.DateTimeFormat('en-IN', {
              timeZone: 'Asia/Kolkata',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            }).format(created)
          : '-',
    };
  };

  // Process invoice data for display
  useEffect(() => {
    const processData = () => {
      // Use search results if search is active, otherwise use regular invoices
      const dataSource = searchValue.trim() ? searchResults : invoices;

      // Safely handle invoices - ensure it's an array
      const filteredData = Array.isArray(dataSource) ? [...dataSource] : [];

      // Format data for table
      const formattedData: FormattedBillingItem[] = filteredData.map(invoice => {
        const totalProjects = invoice.paymentSummary?.totalProjects ?? invoice.items?.length ?? 0;
        const paymentReceivedProjects = invoice.paymentSummary?.paymentReceivedProjects ?? 0;
        const allProjectsPaymentReceived =
          invoice.paymentSummary?.allProjectsPaymentReceived ??
          (totalProjects > 0 && paymentReceivedProjects === totalProjects);
        const hasAnyPaymentReceived =
          invoice.paymentSummary?.hasAnyPaymentReceived ?? paymentReceivedProjects > 0;
        const hasPartialPaymentReceived =
          invoice.paymentSummary?.hasPartialPaymentReceived ??
          (hasAnyPaymentReceived && !allProjectsPaymentReceived);

        const paymentState: FormattedBillingItem['paymentState'] = allProjectsPaymentReceived
          ? 'paid'
          : hasPartialPaymentReceived
            ? 'partial'
            : 'unpaid';

        return {
          id: invoice.id,
          mId: invoice.items?.map(item => item.project.manakProjectId || item.project.projectNumber) || [],
          customerName: invoice.billedTo?.companyName || 'N/A',
          billNumber:
            invoice.formattedInvoiceNumber ||
            `${invoice.invoiceSeries}/${invoice.financialYear}/${invoice.invoiceNumber}`,
          status: 'Billed' as const,
          paymentState,
          paymentSummary: {
            totalProjects,
            paymentReceivedProjects,
            allProjectsPaymentReceived,
            hasAnyPaymentReceived,
            hasPartialPaymentReceived,
          },
          billingDate: formatBillingDateTime(invoice.invoiceDate, invoice.createdAt),
          billingDateRaw: invoice.invoiceDate || invoice.createdAt,
          projectNames: invoice.items?.map(item => item.project.projectName) || [],
          subtotal: invoice.subtotal,
          balanceDue: invoice.balanceDue,
        };
      });

      setDisplayData(formattedData);
    };

    processData();
  }, [invoices, searchResults, searchValue, fetchingInvoices, searchingInvoices]);

  // Status colors mapping
  const statusColors = {
    Billed: 'bg-green-100 text-green-800',
  };

  // Status icons mapping
  const statusIcons = {
    Billed: <BsCurrencyRupee className="w-4 h-4" />,
  };

  // Table column definitions matching the image exactly
  const columns = [
    {
      key: 'mId',
      header: 'M-ID',
      icon: <GoHash />,
      width: '10%',
      headerClassName: 'font-bold text-sm text-red-500',
      cellClassName: 'relative overflow-visible',
      renderCell: value => <ProjectIdDropdown mIds={value} />,
    },
    {
      key: 'customerName',
      header: 'Customer Name',
      icon: <BsBuilding />,
      width: '18%',
      headerClassName: 'font-bold text-sm text-red-500',
      truncate: true,
    },
    {
      key: 'billNumber',
      header: 'Bill Number',
      icon: <FiFileText />,
      width: '14%',
      headerClassName: 'font-bold text-sm text-red-500',
    },
    {
      key: 'subtotal',
      header: 'Sub Total Amount',
      icon: <BsCurrencyRupee />,
      width: '12%',
      headerClassName: 'font-bold text-sm text-red-500',
      renderCell: value => (
        <span className="font-semibold text-gray-800">
          ₹{Number(value || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      key: 'billingDate',
      header: 'Invoice Date',
      icon: <FiCalendar />,
      width: '12%',
      headerClassName: 'font-bold text-sm text-red-500',
      renderCell: (value, row) => (
        <div className="flex flex-col">
          <span title={row.billingDateRaw || ''}>{value.date}</span>
          <span className="text-xs text-gray-500" title={row.billingDateRaw || ''}>
            {value.time}
          </span>
        </div>
      ),
    },
    {
      key: 'paymentSummary',
      header: 'Payment',
      icon: <BsCurrencyRupee />,
      width: '10%',
      headerClassName: 'font-bold text-sm text-red-500',
      renderCell: (_value, row) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
            row.paymentState === 'partial'
              ? 'bg-amber-100 text-amber-800'
              : row.paymentState === 'paid'
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-slate-100 text-slate-700'
          }`}
          title={`${row.paymentSummary.paymentReceivedProjects}/${row.paymentSummary.totalProjects} projects paid`}
        >
          {row.paymentSummary.paymentReceivedProjects}/{row.paymentSummary.totalProjects} Paid
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      width: isSuperAdmin ? '14%' : '12%',
      headerClassName: 'font-bold text-sm text-red-500',
      renderCell: (value, row) => (
        <div className="flex items-center gap-1.5">
          <button
            className="text-green-600 border border-green-300 bg-green-50 rounded-full px-2.5 py-1 text-xs flex items-center hover:bg-green-100 transition-colors font-medium whitespace-nowrap"
            onClick={e => {
              e.stopPropagation();
              navigate(`/edit-invoice/${row.id}`);
            }}
          >
            <TbEdit className="mr-1" /> Edit
          </button>
          <button
            className="text-blue-600 border border-blue-300 bg-blue-50 rounded-full p-1.5 text-xs flex items-center justify-center hover:bg-blue-100 transition-colors"
            onClick={e => {
              e.stopPropagation();
              handleDownloadInvoice(row);
            }}
            title="Download Invoice"
          >
            <FiDownload className="w-3.5 h-3.5" />
          </button>
          {isSuperAdmin && (
            <button
              className="text-red-600 border border-red-300 bg-red-50 rounded-full p-1.5 text-xs flex items-center justify-center hover:bg-red-100 transition-colors"
              onClick={e => {
                e.stopPropagation();
                handleDeleteClick(row);
              }}
              title="Delete Invoice"
            >
              <TbTrash className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  // Handler for downloading invoice
  const handleDownloadInvoice = async (row: FormattedBillingItem) => {
    try {
      console.log('Downloading invoice:', row.billNumber);

      // Show loading state (optional - could add a loading spinner)
      const response = await axiosInstance.get(`/v1/admin/invoice/${row.id}/download`, {
        responseType: 'blob',
      });

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${row.billNumber.replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log('Invoice downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading invoice:', error);
      alert(error.response?.data?.message || 'Failed to download invoice. Please try again.');
    }
  };

  // Handler for initiating delete (Super Admin only)
  const handleDeleteClick = (row: FormattedBillingItem) => {
    setDeleteInvoiceData({
      id: row.id,
      billNumber: row.billNumber,
      customerName: row.customerName,
    });
    setDeleteStep('confirm');
    setOtpValue('');
    setOtpError('');
    setShowDeleteModal(true);
  };

  // Handler for sending OTP
  const handleSendOTP = async () => {
    if (!deleteInvoiceData) return;

    setIsSendingOTP(true);
    setOtpError('');

    try {
      await dispatch(sendDeleteInvoiceOTP(deleteInvoiceData.id)).unwrap();
      toast.success('OTP sent to your email address');
      setDeleteStep('otp');
    } catch (error: any) {
      toast.error(error || 'Failed to send OTP');
      setOtpError(error || 'Failed to send OTP');
    } finally {
      setIsSendingOTP(false);
    }
  };

  // Handler for verifying OTP and deleting invoice
  const handleDeleteWithOTP = async () => {
    if (!deleteInvoiceData || !otpValue) return;

    if (otpValue.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsDeleting(true);
    setOtpError('');

    try {
      await dispatch(deleteInvoiceWithOTP({ invoiceId: deleteInvoiceData.id, otp: otpValue })).unwrap();
      toast.success(`Invoice ${deleteInvoiceData.billNumber} deleted successfully`);
      setShowDeleteModal(false);
      setDeleteInvoiceData(null);
      // Refresh the invoice list with currently active filters
      const refreshParams: {
        page: number;
        limit: number;
        series: 'MESPL' | 'MESS' | 'ALL';
        startDate?: string;
        endDate?: string;
        excludePaymentReceived: boolean;
        includePaymentReceived: boolean;
        includeDuePaymentPending: boolean;
      } = {
        page: currentPage,
        limit: itemsPerPage,
        series: selectedSeries,
        excludePaymentReceived,
        includePaymentReceived,
        includeDuePaymentPending,
      };
      if (startDateParam) {
        refreshParams.startDate = startDateParam;
      }
      if (endDateParam) {
        refreshParams.endDate = endDateParam;
      }

      if (searchValue.trim()) {
        dispatch(
          searchInvoices({
            ...refreshParams,
            search: searchValue.trim(),
          })
        );
      } else {
        dispatch(fetchInvoices(refreshParams));
      }
    } catch (error: any) {
      toast.error(error || 'Failed to delete invoice');
      setOtpError(error || 'Invalid OTP. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handler for closing delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteInvoiceData(null);
    setDeleteStep('confirm');
    setOtpValue('');
    setOtpError('');
  };

  // Handler for row clicks - shows invoice popup
  const handleRowClick = async (row: FormattedBillingItem) => {
    console.log('Billing row clicked:', row);

    try {
      // Fetch actual invoice data from API
      const response = await axiosInstance.get(`/v1/admin/invoice/${row.id}`);
      const invoice = response.data.data;

      console.log('Fetched invoice data:', invoice);

      // Get company details based on invoice series
      const companyDetailsMap: Record<string, { name: string; address: string; email: string; phone: string; gst: string; cin?: string }> = {
        MESS: {
          name: 'Manak Engineering and Software Solutions',
          address: 'C-9/6665, Sector-21, Rohini\nNew Delhi, Delhi - 110086, India',
          email: 'info@manakengineering.com',
          phone: '9871638842',
          gst: '07AFBPJ6861Q1ZV',
        },
        MESPL: {
          name: 'Manak Engineering Services Pvt Ltd',
          address: 'C-9/6665, Sector-21, Rohini\nNew Delhi, Delhi - 110086, India',
          email: 'info@manakengineering.com',
          phone: '9871638842',
          gst: '07AAQCM7695A1Z1',
          cin: 'U71100DL2023PTC412812',
        },
      };

      const companyDetails = companyDetailsMap[invoice.invoiceSeries as 'MESS' | 'MESPL'] || companyDetailsMap.MESPL;

      // Format customer address
      const customerAddress = invoice.billedTo?.clientAddress
        ? `${invoice.billedTo.clientAddress.address || ''}\n${invoice.billedTo.clientAddress.city || ''} - ${invoice.billedTo.clientAddress.zipCode || ''}, ${invoice.billedTo.clientAddress.state || ''}, ${invoice.billedTo.clientAddress.country || ''}`
        : 'Address not available';

      // Map line items from invoice items
      const lineItems: InvoiceLineItem[] = invoice.items?.map((item: any, index: number) => ({
        id: index + 1,
        description: item.project?.projectName || 'N/A',
        mid: item.project?.manakProjectId != null ? `M${item.project.manakProjectId}` : 'N/A',
        articleNo: item.project?.projectNumber || 'N/A',
        quantity: item.quantity || 1,
        rate: item.rate || 0,
        amount: item.lineTotal || 0,
      })) || [];

      // Calculate tax percentage - fixed at 18% to match CreateInvoice and EditInvoice
      // Previously calculated from stored tax amounts which caused rounding errors
      const taxAmount = invoice.tax + invoice.cgst + invoice.sgst;
      const taxPercent = 18; // Tax percentage is always 18%

      // Calculate discount percentage
      const discountPercent = invoice.subtotal > 0 ? Math.round((invoice.discount / invoice.subtotal) * 100) : 0;

      // Format invoice data
      const invoiceData: InvoiceData = {
        invoiceNumber: invoice.formattedInvoiceNumber || `${invoice.invoiceSeries}/${invoice.financialYear}/${invoice.invoiceNumber}`,
        invoiceDate: new Date(invoice.invoiceDate).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
        dueDate: invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }) : '-',
        purchaseOrder: invoice.purchaseOrder || '-',
        terms: invoice.term || 'Due on Receipt',
        taxType: invoice.taxType || 'IGST',
        companyDetails,
        customerDetails: {
          name: invoice.billedTo?.companyName || 'N/A',
          contactPerson: invoice.billedTo?.ownerName || '-',
          address: customerAddress,
          gst: invoice.billedTo?.gstNumber || '-',
        },
        lineItems,
        subtotal: invoice.subtotal || 0,
        discount: invoice.discount || 0,
        discountPercent,
        tax: taxAmount,
        taxPercent,
        cgst: invoice.cgst || 0,
        sgst: invoice.sgst || 0,
        amountPaid: invoice.amountPaid || 0,
        grandTotal: invoice.totalAmount || 0,
        totalInWords: numberToWords(invoice.totalAmount || 0),
        notes: invoice.notes || 'Thank you for your business.',
        termsAndCondition: invoice.termsAndConditions || 'All disputes are subject to Delhi Jurisdiction only.',
      };

      setSelectedInvoice(invoiceData);
      invoicePopup.openPopup();
    } catch (error: any) {
      console.error('Error fetching invoice details:', error);
      alert(error.response?.data?.message || 'Failed to fetch invoice details. Please try again.');
    }
  };

  // If user doesn't have permission, show access denied
  if (!hasBillingPermission) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <IoClose className="text-6xl text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-700 mb-2">Access Denied</h2>
        <p className="text-gray-500 text-center max-w-md">
          You do not have permission to view billing history. Please contact your administrator for access.
        </p>
      </div>
    );
  }

  return (
    <>
      <ComponentWrapper title={
        <div className="flex items-center justify-between w-full">
          <span>Billing History</span>
          {/* Series Dropdown - Modern Red UI */}
          <div className="relative">
            <select
              value={selectedSeries}
              onChange={(e) => {
                setSelectedSeries(e.target.value as 'MESPL' | 'MESS' | 'ALL');
              }}
              className="appearance-none px-5 pr-10 text-sm font-semibold text-red-600 border-2 border-red-500 rounded-xl bg-red-50 hover:bg-red-100 focus:ring-2 focus:ring-red-300 focus:border-red-600 outline-none cursor-pointer transition-all duration-200 shadow-sm"
            >
              <option value="ALL">All Series (MESPL + MESS)</option>
              <option value="MESPL">Manak Engineering Services Pvt. Ltd. (MESPL)</option>
              <option value="MESS">Manak Engineering and Software Service (MESS)</option>
            </select>
            {/* Custom dropdown arrow */}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      }>
        <div className="flex flex-col space-y-4">
          {/* Search and Filter Section */}
          <div className="flex items-center gap-4 bg-white rounded-lg">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search by Customer Name, M-ID, or Bill Number..."
                value={searchInputValue}
                onChange={e => handleSearchInputChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {searchingInvoices ? (
                  <svg
                    className="w-4 h-4 text-blue-500 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                )}
              </div>
            </div>

            {/* Date Range Picker */}
            {!includeDuePaymentPending && (
              <div className="relative" ref={calendarRef}>
                <button
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border-2 transition-all duration-200 ${
                    dateRange.startDate && dateRange.endDate
                      ? 'bg-red-50 border-red-500 text-red-600 hover:bg-red-100'
                      : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <BsCalendar2 className="w-4 h-4" />
                  {dateRange.startDate && dateRange.endDate
                    ? `${formatDateForDisplay(dateRange.startDate)} - ${formatDateForDisplay(dateRange.endDate)}`
                    : 'Filter by Date'}
                </button>

                {showDatePicker && (
                  <div className="absolute right-0 mt-2 bg-white border-2 border-gray-100 rounded-xl shadow-lg p-4 z-50 w-80">
                    {/* Month Navigation */}
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={() => changeMonth(-1)}
                        className="p-1 rounded-full hover:bg-gray-100"
                      >
                        <MdChevronLeft size={20} />
                      </button>
                      <div className="font-medium">
                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </div>
                      <button
                        onClick={() => changeMonth(1)}
                        className="p-1 rounded-full hover:bg-gray-100"
                      >
                        <MdChevronRight size={20} />
                      </button>
                    </div>

                    {/* Day Headers */}
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                        <div key={day} className="text-center text-xs text-gray-500 font-medium">
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="grid grid-cols-7 gap-1">
                      {generateCalendar().map((date, index) => {
                        const isTempSelected = tempDate && date && isSameDay(date, tempDate);
                        const isStart = !selecting && date && dateRange.startDate && isSameDay(date, dateRange.startDate);
                        const isEnd = !selecting && date && dateRange.endDate && isSameDay(date, dateRange.endDate);
                        const isWithinRange = !selecting && date && isInRange(date) && !isStart && !isEnd;

                        return (
                          <div
                            key={index}
                            className={`h-8 w-8 flex items-center justify-center text-sm cursor-pointer rounded-full
                              ${date ? 'hover:bg-red-100' : ''}
                              ${isStart ? 'bg-red-600 text-white' : ''}
                              ${isEnd ? 'bg-red-600 text-white' : ''}
                              ${isTempSelected ? 'bg-red-600 text-white' : ''}
                              ${isWithinRange ? 'bg-red-100' : ''}
                            `}
                            onClick={() => date && handleDateClick(date)}
                          >
                            {date ? date.getDate() : ''}
                          </div>
                        );
                      })}
                    </div>

                    {/* Footer */}
                    <div className="mt-4 flex justify-between items-center">
                      <div className="text-xs text-gray-500">
                        {selecting === 'start'
                          ? 'Select start date'
                          : selecting === 'end'
                            ? 'Select end date'
                            : dateRange.startDate && dateRange.endDate
                              ? 'Range selected'
                              : 'Click to select range'}
                      </div>
                      <div className="flex gap-2">
                        {(dateRange.startDate || dateRange.endDate) && (
                          <button
                            onClick={resetDateRange}
                            className="bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-300 transition-colors"
                          >
                            Clear
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelecting(null);
                            setTempDate(null);
                            setShowDatePicker(false);
                          }}
                          className="bg-red-600 text-white px-4 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reset All Filters Button - Only show when filters are active */}
            {hasActiveFilters && (
              <button
                onClick={resetAllFilters}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border-2 border-gray-300 bg-gray-50 text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-all duration-200"
                title="Reset all filters to defaults"
              >
                <MdRefresh className="w-4 h-4" />
                Reset Filters
              </button>
            )}
          </div>

          {/* Billing History Table */}
          <div className="bg-white rounded-lg">
            {(fetchingInvoices || searchingInvoices) ? (
              <TableSkeleton rows={itemsPerPage} />
            ) : displayData.length > 0 ? (
              <ResponsiveTable
                data={displayData}
                columns={columns}
                onRowClick={handleRowClick}
                statusColors={statusColors}
                statusIcons={statusIcons}
                itemsPerPageOptions={[10, 15, 20, 50]}
                defaultItemsPerPage={itemsPerPage}
                totalItems={(searchValue.trim() ? searchPagination : pagination)?.total || 0}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={size => {
                  setItemsPerPage(size);
                }}
                theadClassName="bg-red-50 rounded-t-lg overflow-hidden"
                thClassName="px-4 py-3 text-left text-sm font-bold uppercase tracking-wider"
              />
            ) : (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No billing history found</h3>
                <p className="text-gray-500">
                  {searchValue.trim() || (dateRange.startDate && dateRange.endDate)
                    ? `Try adjusting your search criteria${includeDuePaymentPending ? '' : ' or date range filter'}`
                    : error || 'No invoices have been generated yet'}
                </p>
                {!includeDuePaymentPending && (dateRange.startDate && dateRange.endDate) && (
                  <button
                    onClick={resetDateRange}
                    className="mt-3 text-red-600 hover:text-red-700 text-sm font-medium underline"
                  >
                    Clear date filter
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </ComponentWrapper>

      {/* Invoice Popup */}
      {selectedInvoice && (
        <PopupWrapper
          title={<span className="text-2xl font-semibold">Invoice Details</span>}
          isOpen={invoicePopup.isOpen}
          onClose={invoicePopup.closePopup}
          size="xl"
          className="!p-6"
        >
          <div className="bg-white pt-2">
            {/* Invoice Header */}
            <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-8 mb-6">
              <div className="min-w-0">
                <h1 className="text-3xl font-bold text-red-600 mb-2">
                  {selectedInvoice.companyDetails.name}
                </h1>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="whitespace-pre-line">
                    {selectedInvoice.companyDetails.address}
                  </div>
                  <div>
                    {selectedInvoice.companyDetails.email} | {selectedInvoice.companyDetails.phone}
                  </div>
                  <div>GST IN - {selectedInvoice.companyDetails.gst}</div>
                  {selectedInvoice.companyDetails.cin && (
                    <div>CIN No - {selectedInvoice.companyDetails.cin}</div>
                  )}
                </div>
              </div>
              <div className="text-left md:text-right md:pl-6">
                <div className="text-sm text-gray-500 mb-1">INVOICE NUMBER</div>
                <div className="text-3xl font-bold">{selectedInvoice.invoiceNumber}</div>
              </div>
            </div>

            {/* Billing Information */}
            <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-8 mb-8">
              <div>
                <div className="text-sm font-semibold text-gray-700 mb-2">BILLED TO</div>
                <div className="font-semibold text-lg mb-2">
                  {selectedInvoice.customerDetails.name}
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>{selectedInvoice.customerDetails.contactPerson}</div>
                  <div className="whitespace-pre-line">
                    {selectedInvoice.customerDetails.address}
                  </div>
                  <div>GSTIN - {selectedInvoice.customerDetails.gst}</div>
                </div>
              </div>
              <div className="space-y-3 md:pl-6">
                <div className="grid grid-cols-[150px_1fr] gap-4 items-center">
                  <span className="text-sm text-gray-600">Invoice Date</span>
                  <span className="font-semibold text-left md:text-right">{selectedInvoice.invoiceDate}</span>
                </div>
                <div className="grid grid-cols-[150px_1fr] gap-4 items-center">
                  <span className="text-sm text-gray-600">Terms</span>
                  <span className="font-semibold text-left md:text-right">{selectedInvoice.terms}</span>
                </div>
                <div className="grid grid-cols-[150px_1fr] gap-4 items-center">
                  <span className="text-sm text-gray-600">Due Date</span>
                  <span className="font-semibold text-left md:text-right">{selectedInvoice.dueDate}</span>
                </div>
                <div className="grid grid-cols-[150px_1fr] gap-4 items-center">
                  <span className="text-sm text-gray-600">Purchase Order (P.O)</span>
                  <span className="font-semibold text-left md:text-right">{selectedInvoice.purchaseOrder}</span>
                </div>
              </div>
            </div>

            {/* Line Items Table */}
            <div className="mb-8">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">
                      #
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold">
                      ITEM & DESCRIPTION
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold">
                      QTY
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-right text-sm font-semibold">
                      RATE
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-right text-sm font-semibold">
                      AMOUNT
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.lineItems.map(item => (
                    <tr key={item.id}>
                      <td className="border border-gray-300 px-4 py-3 text-sm">{item.id}</td>
                      <td className="border border-gray-300 px-4 py-3 text-sm">
                        <div className="font-medium">{item.description}</div>
                        <div className="text-gray-600 text-xs">
                          <span className="font-medium">MID:</span> {item.mid}
                          <span className="text-gray-400 mx-1.5">|</span>
                          <span className="font-medium">Article No:</span> {item.articleNo}
                        </div>
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-center text-sm">
                        {item.quantity}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-right text-sm">
                        ₹{item.rate.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-right text-sm">
                        ₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals Section */}
            <div className="flex justify-end mb-8">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>
                    ₹
                    {selectedInvoice.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Discount ({selectedInvoice.discountPercent}%)</span>
                  <span>
                    -₹
                    {selectedInvoice.discount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {selectedInvoice.taxType === 'IGST' ? (
                  <div className="flex justify-between">
                    <span>IGST ({selectedInvoice.taxPercent}%)</span>
                    <span>
                      ₹{selectedInvoice.tax.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span>CGST ({selectedInvoice.taxPercent / 2}%)</span>
                      <span>
                        ₹{(selectedInvoice.cgst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>SGST ({selectedInvoice.taxPercent / 2}%)</span>
                      <span>
                        ₹{(selectedInvoice.sgst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span>Amount Paid</span>
                  <span>
                    ₹
                    {selectedInvoice.amountPaid.toLocaleString('en-IN', {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="border-t border-gray-300 pt-2">
                  <div className="flex justify-between font-bold text-lg">
                    <span>Grand Total</span>
                    <span>
                      ₹
                      {selectedInvoice.grandTotal.toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Total in Words */}
            <div className="mb-8">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total In Words</span>
                <span className="italic">{selectedInvoice.totalInWords}</span>
              </div>
            </div>

            {/* Notes and Terms */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="font-semibold mb-2">Notes</h3>
                <p className="text-sm text-gray-600">{selectedInvoice.notes}</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Terms & Condition</h3>
                <p className="text-sm text-gray-600">{selectedInvoice.termsAndCondition}</p>
              </div>
            </div>

            {/* Download Button - Full Width */}
            <div className="w-full">
              <button className="w-full bg-red-600 text-white px-8 py-4 rounded-lg hover:bg-red-700 transition-colors font-semibold flex items-center justify-center gap-2 text-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download Invoice
              </button>
            </div>
          </div>
        </PopupWrapper>
      )}

      {/* Delete Invoice Modal (Super Admin only) */}
      <AnimatePresence>
        {showDeleteModal && deleteInvoiceData && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={handleCloseDeleteModal}
            />

            {/* Centered Modal Box */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-full max-w-md bg-white shadow-xl rounded-xl flex flex-col overflow-hidden">
              {/* Modal Content */}
              <div className="flex-1 p-6 text-left">
                {deleteStep === 'confirm' ? (
                  <>
                    {/* Confirmation Step */}
                    <div className="mb-4">
                      <h2 className="text-xl font-bold text-gray-900 mb-1">
                        Delete Invoice?
                      </h2>
                      <p className="text-gray-500 text-sm leading-relaxed">
                        Are you sure you want to delete invoice <span className="font-semibold text-gray-900">{deleteInvoiceData.billNumber}</span>?
                      </p>
                    </div>

                    <div className="mb-6">
                      <p className="text-sm text-yellow-800 font-medium mb-1">
                        ⚠️ This action cannot be undone
                      </p>
                      <ul className="space-y-1 text-sm text-yellow-700 list-disc list-inside ml-1">
                        <li>Permanently removes the invoice</li>
                        <li>Clears billing status from projects</li>
                        <li>Removes all associated payment records</li>
                      </ul>
                    </div>

                    <p className="text-sm text-gray-400 mb-2 text-left">
                      For security, we'll send a one-time password (OTP) to your email address.
                    </p>
                  </>
                ) : (
                  <>
                    {/* OTP Entry Step */}
                    <div className="mb-4">
                      <h2 className="text-xl font-bold text-gray-900 mb-1">
                        Verify Deletion
                      </h2>
                      <p className="text-gray-500 text-sm leading-relaxed">
                        We've sent a 6-digit OTP to your email address.
                      </p>
                    </div>

                    {/* OTP Input */}
                    <div className="mb-6">
                      <input
                        type="text"
                        value={otpValue}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtpValue(value);
                          setOtpError('');
                        }}
                        placeholder="000000"
                        className={`w-full px-4 py-3 text-center text-2xl font-bold tracking-[0.3em] border ${
                          otpError ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 focus:border-red-500 text-gray-800'
                        } rounded-lg focus:outline-none focus:ring-0 transition-all placeholder-gray-300`}
                        maxLength={6}
                        autoFocus
                      />
                      {otpError && (
                        <p className="mt-1 text-sm text-red-600 text-left font-medium">{otpError}</p>
                      )}
                    </div>

                    <p className="text-sm text-gray-500 text-left">
                      Didn't receive the code?{' '}
                      <button
                        type="button"
                        onClick={handleSendOTP}
                        disabled={isSendingOTP}
                        className="text-red-600 hover:text-red-700 hover:underline font-semibold"
                      >
                        Resend OTP
                      </button>
                    </p>
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-6 pt-0 flex flex-col gap-3">
                {deleteStep === 'confirm' ? (
                  <button
                    type="button"
                    onClick={handleSendOTP}
                    disabled={isSendingOTP}
                    className="w-full py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSendingOTP ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending OTP...
                      </>
                    ) : (
                      'Send OTP & Continue'
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleDeleteWithOTP}
                    disabled={isDeleting || otpValue.length !== 6}
                    className="w-full py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isDeleting ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Deleting...
                      </>
                    ) : (
                      'Delete Invoice Permanently'
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleCloseDeleteModal}
                  className="w-full py-2.5 border border-gray-200 text-gray-600 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default BillingHistory;
